import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { installationApi, InstallationRequest } from '../services/installationApi';
import { PageShell } from '../components/pageLayout';

interface InstallationWaitingProps {
  installationRequest?: InstallationRequest;
}

const waitingShellSx = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 0 }
} as const;

const InstallationWaiting: React.FC<InstallationWaitingProps> = ({ installationRequest }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const [request, setRequest] = useState<InstallationRequest | undefined>(installationRequest);
  const [loading, setLoading] = useState(!installationRequest);

  useEffect(() => {
    const fetchInstallationRequest = async () => {
      if (installationRequest) {
        setLoading(false);
        return;
      }
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await installationApi.getInstallationRequestByUserId(currentUser.id);
        if (response.data) {
          setRequest(response.data);
        }
      } catch (error: any) {
        console.error('세팅 일정 조회 실패:', error);
        if (error.response?.status !== 404) {
          console.error('세팅 일정 조회 중 오류 발생:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInstallationRequest();
  }, [currentUser?.id, installationRequest]);

  const handleKakaoInquiry = () => {
    window.open('https://pf.kakao.com/_your_channel_id', '_blank');
  };

  if (loading) {
    return (
      <PageShell
        fullBleed
        sx={{
          ...waitingShellSx,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <CircularProgress />
      </PageShell>
    );
  }

  return (
    <PageShell fullBleed sx={{ ...waitingShellSx, background: '#1a2332' }}>
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 1,
            textAlign: 'center'
          }}
        >
          <AccessTimeIcon
            sx={{
              fontSize: { xs: 56, md: 64 },
              color: theme.palette.warning.main,
              mb: 2
            }}
          />

          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            세팅 완료 후, 이용이 가능합니다
          </Typography>

          {request && (request.status === 'confirmed' || request.status === 'pending') ? (
            <>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    세팅 일정
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ ml: 4, mb: 1 }}>
                  날짜: {new Date(request.scheduledDate).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
                <Typography variant="body2" sx={{ ml: 4 }}>
                  시간: {request.scheduledTime}
                </Typography>
                {request.assignedTo && (
                  <Typography variant="body2" sx={{ ml: 4, mt: 1 }}>
                    담당자: {request.assignedTo}
                    {request.assignedToPhone && ` (${request.assignedToPhone})`}
                  </Typography>
                )}
              </Alert>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  세팅일정 변경을 원할 경우, 아래의 카톡으로 문의 주세요.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MessageIcon />}
                  onClick={handleKakaoInquiry}
                  sx={{
                    minWidth: { xs: '100%', sm: 200 },
                    py: { xs: 1.5, md: 2 },
                    backgroundColor: '#FEE500',
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: '#FDD835'
                    }
                  }}
                >
                  카카오톡 문의
                </Button>
              </Box>
            </>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                세팅 일정이 확정되지 않았습니다.
                <br />
                담당자가 연락드릴 예정입니다.
              </Typography>
            </Alert>
          )}
        </Paper>
      </Container>
    </PageShell>
  );
};

export default InstallationWaiting;
